import { FC } from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { TPgJsonObject } from 'services/api/paymentGateway/types';
import { toPrettyJson } from '../utils';

/** Counters a reader looks for first. */
const HIGHLIGHT = new Set([
	'invoices_processed',
	'dd_initiated',
	'card_succeeded',
	'card_failed',
	'dry_run_dd_would_charge',
	'dry_run_card_would_charge',
	'errors',
]);

/** Counters of a run (`recurring_runs.stats`: `{ "invoices_processed": 12, … }`). */
export const RunStats: FC<{ stats: TPgJsonObject | null | undefined }> = ({ stats }) => {
	const entries = Object.entries(stats ?? {});
	if (!entries.length) {
		return (
			<Typography variant="body2" color="text.secondary">
				No counters yet.
			</Typography>
		);
	}
	const numeric = entries.filter(([, value]) => typeof value === 'number');
	const other = entries.filter(([, value]) => typeof value !== 'number');
	return (
		<Box>
			<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
				{numeric
					.sort(
						([a], [b]) => Number(HIGHLIGHT.has(b)) - Number(HIGHLIGHT.has(a)) || a.localeCompare(b),
					)
					.map(([key, value]) => (
						<Chip
							key={key}
							size="small"
							label={`${key.replace(/_/g, ' ')}: ${String(value)}`}
							color={
								key === 'errors' && Number(value) > 0
									? 'error'
									: HIGHLIGHT.has(key)
										? 'primary'
										: 'default'
							}
							variant={HIGHLIGHT.has(key) ? 'filled' : 'outlined'}
						/>
					))}
			</Box>
			{other.length > 0 && (
				<Box
					component="pre"
					sx={{
						fontSize: '0.75rem',
						mt: 1,
						mb: 0,
						whiteSpace: 'pre-wrap',
						wordBreak: 'break-word',
					}}
				>
					{toPrettyJson(Object.fromEntries(other))}
				</Box>
			)}
		</Box>
	);
};

/** One-line summary of the main counters for the runs table. */
export const summarizeStats = (stats: TPgJsonObject | null | undefined): string => {
	const entries = Object.entries(stats ?? {}).filter(
		([key, value]) => HIGHLIGHT.has(key) && typeof value === 'number' && value !== 0,
	);
	return entries.length
		? entries.map(([key, value]) => `${key.replace(/_/g, ' ')}: ${String(value)}`).join(', ')
		: '—';
};
